import { useState, useEffect } from "react"
import AdminSidebar from "../../components/admin/AdminSidebar"
import AdminHeader from "../../components/admin/AdminHeader"
import { usePromoCodes } from "../../hooks/usePromoCodes"
import { toastUtils } from "../../utils/toast"
import { 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  CheckCircle,
  XCircle,
  Calendar,
  Percent,
  Users,
  Tag,
  X,
  Hash,
  FileText,
  Infinity
} from "lucide-react"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import { Textarea } from "../../components/ui/textarea"

export default function PromoCodesManagementPage() {
  const [searchTerm, setSearchTerm] = useState("")
  const [filterActive, setFilterActive] = useState("all")
  const [page, setPage] = useState(1)
  const [limit] = useState(10)
  const [promoCodes, setPromoCodes] = useState([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [selectedPromoCode, setSelectedPromoCode] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    code: "",
    description: "",
    discountPercentage: "",
    validFrom: "",
    validUntil: "",
    maxUses: "",
    isActive: true
  })
  const [formErrors, setFormErrors] = useState({})

  const { loading, getPromoCodes, deletePromoCode, updatePromoCode, createPromoCode } = usePromoCodes()

  useEffect(() => {
    loadPromoCodes()
  }, [page, searchTerm, filterActive])

  const loadPromoCodes = async () => {
    try {
      const filters = {
        page,
        limit,
        search: searchTerm || undefined,
        isActive: filterActive === "all" ? undefined : filterActive === "active"
      }
      
      const response = await getPromoCodes(filters)
      
      if (response) {
        if (Array.isArray(response)) {
          setPromoCodes(response)
          setTotal(response.length)
        } else {
          setPromoCodes(response.data || response.categories || [])
          setTotal(response.total || 0)
          setTotalPages(response.totalPages || 1)
        }
      }
    } catch (error) {
      toastUtils.error(error?.message || "Erreur lors du chargement des codes promo")
    }
  }

  const handleDelete = async (promoCode) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le code promo "${promoCode.code}" ?`)) {
      return
    }

    try {
      await deletePromoCode(promoCode.id)
      toastUtils.success("Code promo supprimé avec succès")
      loadPromoCodes()
    } catch (error) {
      toastUtils.error(error?.message || "Erreur lors de la suppression")
    }
  }

  const handleToggleActive = async (promoCode) => {
    try {
      await updatePromoCode(promoCode.id, { isActive: !promoCode.isActive })
      toastUtils.success(`Code promo ${promoCode.isActive ? 'désactivé' : 'activé'} avec succès`)
      loadPromoCodes()
    } catch (error) {
      toastUtils.error(error?.message || "Erreur lors de la mise à jour")
    }
  }

  const handleView = (promoCode) => {
    setSelectedPromoCode(promoCode)
    setIsViewModalOpen(true)
  }

  const handleEdit = (promoCode) => {
    setSelectedPromoCode(promoCode)
    // Formater les dates pour les inputs datetime-local
    const formatDateForInput = (dateString) => {
      if (!dateString) return ""
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day}T${hours}:${minutes}`
    }
    
    setFormData({
      code: promoCode.code || "",
      description: promoCode.description || "",
      discountPercentage: promoCode.discountPercentage?.toString() || "",
      validFrom: formatDateForInput(promoCode.validFrom),
      validUntil: formatDateForInput(promoCode.validUntil),
      maxUses: promoCode.maxUses?.toString() || "",
      isActive: promoCode.isActive !== undefined ? promoCode.isActive : true
    })
    setFormErrors({})
    setIsModalOpen(true)
  }

  const handleCreate = () => {
    setSelectedPromoCode(null)
    // Définir les dates par défaut (aujourd'hui pour début, dans 1 mois pour fin)
    const now = new Date()
    const oneMonthLater = new Date()
    oneMonthLater.setMonth(oneMonthLater.getMonth() + 1)
    
    const formatDateForInput = (date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day}T${hours}:${minutes}`
    }
    
    setFormData({
      code: "",
      description: "",
      discountPercentage: "",
      validFrom: formatDateForInput(now),
      validUntil: formatDateForInput(oneMonthLater),
      maxUses: "",
      isActive: true
    })
    setFormErrors({})
    setIsModalOpen(true)
  }

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    // Effacer l'erreur pour ce champ
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }))
    }
  }

  const validateForm = () => {
    const errors = {}
    
    if (!formData.code.trim()) {
      errors.code = "Le code est requis"
    } else if (formData.code.length < 3) {
      errors.code = "Le code doit contenir au moins 3 caractères"
    }
    
    if (!formData.discountPercentage) {
      errors.discountPercentage = "Le pourcentage de réduction est requis"
    } else {
      const percentage = parseFloat(formData.discountPercentage)
      if (isNaN(percentage) || percentage <= 0 || percentage > 100) {
        errors.discountPercentage = "Le pourcentage doit être entre 1 et 100"
      }
    }
    
    if (!formData.validFrom) {
      errors.validFrom = "La date de début est requise"
    }
    
    if (!formData.validUntil) {
      errors.validUntil = "La date de fin est requise"
    } else if (formData.validFrom && formData.validUntil) {
      const fromDate = new Date(formData.validFrom)
      const untilDate = new Date(formData.validUntil)
      if (untilDate <= fromDate) {
        errors.validUntil = "La date de fin doit être après la date de début"
      }
    }
    
    if (formData.maxUses && formData.maxUses.trim()) {
      const maxUses = parseInt(formData.maxUses, 10)
      if (isNaN(maxUses) || maxUses <= 0) {
        errors.maxUses = "Le nombre maximum d'utilisations doit être un nombre positif"
      }
    }
    
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      toastUtils.error("Veuillez corriger les erreurs du formulaire")
      return
    }
    
    setIsSubmitting(true)
    
    try {
      const promoCodeData = {
        code: formData.code.trim().toUpperCase(),
        description: formData.description.trim() || undefined,
        discountPercentage: parseFloat(formData.discountPercentage),
        validFrom: new Date(formData.validFrom).toISOString(),
        validUntil: new Date(formData.validUntil).toISOString(),
        maxUses: formData.maxUses && formData.maxUses.trim() 
          ? parseInt(formData.maxUses, 10) 
          : undefined,
        isActive: formData.isActive
      }
      
      if (selectedPromoCode) {
        // Modification
        await updatePromoCode(selectedPromoCode.id, promoCodeData)
        toastUtils.success("Code promo modifié avec succès")
      } else {
        // Création
        await createPromoCode(promoCodeData)
        toastUtils.success("Code promo créé avec succès")
      }
      
      handleModalSuccess()
    } catch (error) {
      const errorMessage = Array.isArray(error?.response?.data?.message)
        ? error.response.data.message[0]
        : error?.response?.data?.message || error?.message || "Erreur lors de l'opération"
      toastUtils.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleModalSuccess = () => {
    setIsModalOpen(false)
    setSelectedPromoCode(null)
    setFormData({
      code: "",
      description: "",
      discountPercentage: "",
      validFrom: "",
      validUntil: "",
      maxUses: "",
      isActive: true
    })
    setFormErrors({})
    loadPromoCodes()
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  const isExpired = (validUntil) => {
    if (!validUntil) return false
    return new Date(validUntil) < new Date()
  }

  const isActiveNow = (promoCode) => {
    if (!promoCode.isActive) return false
    const now = new Date()
    const validFrom = promoCode.validFrom ? new Date(promoCode.validFrom) : null
    const validUntil = promoCode.validUntil ? new Date(promoCode.validUntil) : null
    
    if (validFrom && now < validFrom) return false
    if (validUntil && now > validUntil) return false
    
    return true
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <AdminSidebar />
      <div className="flex-1 ml-64 p-8 overflow-y-auto">
        <AdminHeader 
          title="Codes Promo" 
          subtitle="Gérer les codes promo de réduction"
        />

        {/* Actions Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher un code promo..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setPage(1)
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent"
              />
            </div>

            {/* Filter */}
            <select
              value={filterActive}
              onChange={(e) => {
                setFilterActive(e.target.value)
                setPage(1)
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent"
            >
              <option value="all">Tous</option>
              <option value="active">Actifs</option>
              <option value="inactive">Inactifs</option>
            </select>
          </div>

          <Button onClick={handleCreate} className="bg-[#141835] hover:bg-[#203a8a]">
            <Plus size={20} className="mr-2" />
            Créer un code promo
          </Button>
        </div>

        {/* Promo Codes Table */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#141835] mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des codes promo...</p>
          </div>
        ) : promoCodes.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Tag className="mx-auto mb-4 text-gray-400" size={48} />
            <p className="text-gray-600">Aucun code promo trouvé</p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Réduction</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Période</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilisations</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {promoCodes.map((promoCode) => (
                    <tr key={promoCode.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Tag size={16} className="mr-2 text-[#141835]" />
                          <span className="font-medium text-gray-900">{promoCode.code}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {promoCode.description || "Aucune description"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Percent size={16} className="mr-1 text-green-600" />
                          <span className="text-sm font-medium text-gray-900">
                            {promoCode.discountPercentage}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar size={14} className="mr-1" />
                          <div>
                            <div>{formatDate(promoCode.validFrom)}</div>
                            <div className="text-xs">→ {formatDate(promoCode.validUntil)}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <Users size={14} className="mr-1" />
                          {promoCode.usedCount || 0} / {promoCode.maxUses || "∞"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {isActiveNow(promoCode) ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle size={12} className="mr-1" />
                              Actif
                            </span>
                          ) : isExpired(promoCode.validUntil) ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <XCircle size={12} className="mr-1" />
                              Expiré
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              <XCircle size={12} className="mr-1" />
                              Inactif
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleView(promoCode)}
                            className="text-[#141835] hover:text-[#203a8a]"
                            title="Voir les détails"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            onClick={() => handleEdit(promoCode)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Modifier"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleToggleActive(promoCode)}
                            className={promoCode.isActive ? "text-orange-600 hover:text-orange-800" : "text-green-600 hover:text-green-800"}
                            title={promoCode.isActive ? "Désactiver" : "Activer"}
                          >
                            {promoCode.isActive ? <XCircle size={18} /> : <CheckCircle size={18} />}
                          </button>
                          <button
                            onClick={() => handleDelete(promoCode)}
                            className="text-red-600 hover:text-red-800"
                            title="Supprimer"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Page {page} sur {totalPages} ({total} codes promo)
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    variant="outline"
                  >
                    Précédent
                  </Button>
                  <Button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    variant="outline"
                  >
                    Suivant
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Create/Edit Modal */}
        {isModalOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setIsModalOpen(false)}
          >
            <div 
              className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedPromoCode ? "Modifier le code promo" : "Créer un code promo"}
                </h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Code */}
                <div>
                  <Label htmlFor="code" className="text-sm font-medium text-gray-700 mb-2 block flex items-center gap-2">
                    <Hash size={16} />
                    Code <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="code"
                    name="code"
                    value={formData.code}
                    onChange={(e) => {
                      // Convertir en majuscules automatiquement
                      const upperValue = e.target.value.toUpperCase()
                      setFormData(prev => ({ ...prev, code: upperValue }))
                      if (formErrors.code) {
                        setFormErrors(prev => ({ ...prev, code: null }))
                      }
                    }}
                    placeholder="Ex: PROMO2024"
                    required
                    maxLength={50}
                    className={formErrors.code ? "border-red-500" : ""}
                  />
                  {formErrors.code && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.code}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">Le code sera automatiquement converti en majuscules</p>
                </div>

                {/* Description */}
                <div>
                  <Label htmlFor="description" className="text-sm font-medium text-gray-700 mb-2 block flex items-center gap-2">
                    <FileText size={16} />
                    Description
                  </Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    placeholder="Ex: Réduction spéciale pour les nouveaux utilisateurs"
                    rows={3}
                  />
                </div>

                {/* Pourcentage de réduction et Utilisations max */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Pourcentage de réduction */}
                  <div>
                    <Label htmlFor="discountPercentage" className="text-sm font-medium text-gray-700 mb-2 block flex items-center gap-2">
                      <Percent size={16} />
                      Pourcentage de réduction <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="discountPercentage"
                        name="discountPercentage"
                        type="number"
                        min="1"
                        max="100"
                        step="0.01"
                        value={formData.discountPercentage}
                        onChange={handleFormChange}
                        placeholder="10"
                        required
                        className={formErrors.discountPercentage ? "border-red-500 pr-10" : "pr-10"}
                      />
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                    </div>
                    {formErrors.discountPercentage && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.discountPercentage}</p>
                    )}
                  </div>

                  {/* Nombre maximum d'utilisations */}
                  <div>
                    <Label htmlFor="maxUses" className="text-sm font-medium text-gray-700 mb-2 block flex items-center gap-2">
                      <Users size={16} />
                      Utilisations max
                    </Label>
                    <div className="relative">
                      <Input
                        id="maxUses"
                        name="maxUses"
                        type="number"
                        min="1"
                        value={formData.maxUses}
                        onChange={handleFormChange}
                        placeholder="Illimité"
                        className={formErrors.maxUses ? "border-red-500" : ""}
                      />
                      {!formData.maxUses && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                          <Infinity size={16} />
                        </div>
                      )}
                    </div>
                    {formErrors.maxUses && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.maxUses}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">Laisser vide pour un nombre illimité</p>
                  </div>
                </div>

                {/* Dates de validité */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Date de début */}
                  <div>
                    <Label htmlFor="validFrom" className="text-sm font-medium text-gray-700 mb-2 block flex items-center gap-2">
                      <Calendar size={16} />
                      Date de début <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="validFrom"
                      name="validFrom"
                      type="datetime-local"
                      value={formData.validFrom}
                      onChange={handleFormChange}
                      required
                      className={formErrors.validFrom ? "border-red-500" : ""}
                    />
                    {formErrors.validFrom && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.validFrom}</p>
                    )}
                  </div>

                  {/* Date de fin */}
                  <div>
                    <Label htmlFor="validUntil" className="text-sm font-medium text-gray-700 mb-2 block flex items-center gap-2">
                      <Calendar size={16} />
                      Date de fin <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="validUntil"
                      name="validUntil"
                      type="datetime-local"
                      value={formData.validUntil}
                      onChange={handleFormChange}
                      required
                      min={formData.validFrom || undefined}
                      className={formErrors.validUntil ? "border-red-500" : ""}
                    />
                    {formErrors.validUntil && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.validUntil}</p>
                    )}
                  </div>
                </div>

                {/* Statut actif */}
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <input
                    type="checkbox"
                    id="isActive"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleFormChange}
                    className="h-4 w-4 text-[#141835] focus:ring-[#141835] border-gray-300 rounded"
                  />
                  <Label htmlFor="isActive" className="text-sm font-medium text-gray-700 cursor-pointer">
                    Code promo actif
                  </Label>
                  <p className="text-xs text-gray-500 ml-auto">
                    {formData.isActive ? "Le code peut être utilisé" : "Le code est désactivé"}
                  </p>
                </div>

                {/* Aperçu */}
                {formData.code && formData.discountPercentage && (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">Aperçu</Label>
                    <div className="bg-white rounded border border-gray-200 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Tag className="text-[#141835]" size={20} />
                          <span className="font-bold text-lg">{formData.code}</span>
                        </div>
                        <span className="text-lg font-bold text-green-600">
                          -{formData.discountPercentage}%
                        </span>
                      </div>
                      {formData.description && (
                        <p className="text-sm text-gray-600 mb-2">{formData.description}</p>
                      )}
                      <div className="text-xs text-gray-500 flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Calendar size={12} />
                          {formData.validFrom && new Date(formData.validFrom).toLocaleDateString('fr-FR')}
                          {" → "}
                          {formData.validUntil && new Date(formData.validUntil).toLocaleDateString('fr-FR')}
                        </span>
                        {formData.maxUses ? (
                          <span className="flex items-center gap-1">
                            <Users size={12} />
                            Max: {formData.maxUses}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <Infinity size={12} />
                            Illimité
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsModalOpen(false)}
                    disabled={isSubmitting}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-[#141835] hover:bg-[#203a8a]"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {selectedPromoCode ? "Modification..." : "Création..."}
                      </>
                    ) : (
                      selectedPromoCode ? "Modifier" : "Créer"
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* View Modal - TODO: Créer un composant modal dédié */}
        {isViewModalOpen && selectedPromoCode && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-4">Détails du code promo</h2>
              <div className="space-y-4">
                <div>
                  <label className="font-medium">Code:</label>
                  <p>{selectedPromoCode.code}</p>
                </div>
                <div>
                  <label className="font-medium">Description:</label>
                  <p>{selectedPromoCode.description || "Aucune description"}</p>
                </div>
                <div>
                  <label className="font-medium">Réduction:</label>
                  <p>{selectedPromoCode.discountPercentage}%</p>
                </div>
                <div>
                  <label className="font-medium">Période:</label>
                  <p>{formatDate(selectedPromoCode.validFrom)} → {formatDate(selectedPromoCode.validUntil)}</p>
                </div>
                <div>
                  <label className="font-medium">Utilisations:</label>
                  <p>{selectedPromoCode.usedCount || 0} / {selectedPromoCode.maxUses || "Illimité"}</p>
                </div>
              </div>
              <div className="flex justify-end mt-6">
                <Button onClick={() => setIsViewModalOpen(false)}>
                  Fermer
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

